import {
  MessagesSquare, Workflow, LineChart, Building2,
  Globe, Brain, Plug, Users, Languages, BarChart3,
  FileText, GitBranch, RefreshCw, Bell, Calendar,
  Search, Database, FileDown, ShieldCheck, Cpu, Server,
} from 'lucide-react';

export const solutions = {
  'conversational-agents': {
    icon: MessagesSquare,
    eyebrow: 'Conversational Agents',
    title: 'AI agents that talk to your customers — 24/7',
    subtitle:
      'WhatsApp bots, Telegram bots, and web chat agents. Trained on your business, integrated with your systems, always online.',
    included: [
      { icon: Globe, title: 'Multi-platform deployment', desc: 'WhatsApp Business, Telegram, web chat widget — or all three. One agent, multiple channels.' },
      { icon: Brain, title: 'Business knowledge training', desc: "Your agent learns from your FAQs, catalog, policies, and past conversations. It knows your business." },
      { icon: Plug, title: 'CRM & backend integration', desc: "Connects to HubSpot, Salesforce, order management, booking systems, or any internal API. It doesn't just talk — it acts." },
      { icon: Users, title: 'Handoff & escalation', desc: "Smart escalation when it can't resolve. Hands off to a human with full conversation context." },
      { icon: Languages, title: 'Multi-language support', desc: 'Handles conversations in multiple languages — auto-detected, no configuration needed.' },
      { icon: BarChart3, title: 'Analytics dashboard', desc: 'Conversations handled, resolution rate, escalation triggers, satisfaction signals — in real time.' },
    ],
    useCases: [
      'Customer support — 24/7 FAQ handling and ticket creation',
      'Lead qualification and follow-up',
      'Appointment booking and reminders',
      'E-commerce order tracking and support',
      'Onboarding and product guidance',
      'Internal HR / employee self-service',
    ],
    integrations: ['WhatsApp', 'Telegram', 'Web Chat', 'HubSpot', 'Salesforce', 'Slack', 'Custom APIs'],
    industries: ['Customer Support', 'E-commerce', 'HR', 'Healthcare', 'Sales'],
    ctaTitle: 'Want a WhatsApp bot for your business?',
  },

  'workflow-agents': {
    icon: Workflow,
    eyebrow: 'Workflow Agents',
    title: 'AI agents that run your business processes — automatically',
    subtitle:
      "Repetitive multi-step workflows. Document processing. Approval chains. Data entry. Let agents handle it so your team doesn't have to.",
    included: [
      { icon: FileText, title: 'Document processing', desc: 'Intake, parse, validate, and route documents across teams and systems automatically.' },
      { icon: GitBranch, title: 'Approval workflows', desc: 'Multi-step approval chains across teams and tools, with full audit logging.' },
      { icon: RefreshCw, title: 'Cross-system sync', desc: 'Transform and move data between your tools so nothing is entered twice.' },
      { icon: Bell, title: 'Alert pipelines', desc: 'Trigger-based monitoring and notifications routed to the right people.' },
      { icon: Calendar, title: 'Scheduled runs', desc: 'Recurring jobs — reports, reconciliations, batch tasks — on your schedule.' },
      { icon: Plug, title: 'API orchestration', desc: 'Coordinate actions across multiple APIs in a single autonomous flow.' },
    ],
    useCases: [
      'Document intake, processing, and routing',
      'Approval workflows across teams and systems',
      'Data transformation and cross-system sync',
      'Scheduled reports and alert pipelines',
      'Invoice processing and validation',
      'Email triage and response drafting',
    ],
    integrations: ['Google Workspace', 'Microsoft 365', 'Slack', 'Notion', 'Airtable', 'HubSpot', 'Salesforce', 'Custom APIs'],
    industries: ['Logistics', 'HR', 'Insurance', 'Marketing', 'Banking & Fintech'],
    ctaTitle: 'Have a process worth automating?',
  },

  'data-agents': {
    icon: LineChart,
    eyebrow: 'Data & Research Agents',
    title: 'Agents that find, extract, and deliver intelligence — continuously',
    subtitle:
      'Set up once. Get structured data, competitor insights, market signals, and custom reports delivered automatically — forever.',
    included: [
      { icon: Search, title: 'Web monitoring', desc: 'Continuous monitoring and change detection across the sources you care about.' },
      { icon: LineChart, title: 'Competitor tracking', desc: 'Track pricing, product, and content changes across competitors automatically.' },
      { icon: Database, title: 'Structured extraction', desc: 'Turn messy web pages into clean, schema-driven structured data at scale.' },
      { icon: FileText, title: 'Auto-generated reports', desc: 'Scheduled research reports and digests delivered to your team or systems.' },
      { icon: Bell, title: 'Alert systems', desc: 'Get notified the moment a business signal you care about appears.' },
      { icon: FileDown, title: 'Flexible output', desc: 'JSON, CSV, Google Sheets, database, Slack alert, email digest, or API webhook.' },
    ],
    useCases: [
      'Web monitoring and change detection',
      'Competitor tracking (pricing, product, content)',
      'Market intelligence pipelines',
      'Structured data extraction at scale',
      'Auto-generated research reports',
      'Alert systems for business signals',
    ],
    integrations: ['JSON', 'CSV', 'Google Sheets', 'Database', 'Slack', 'Email', 'API Webhook'],
    industries: ['Marketing', 'Legal', 'Logistics', 'E-commerce'],
    ctaTitle: 'Need intelligence delivered on autopilot?',
  },

  enterprise: {
    icon: Building2,
    eyebrow: 'Enterprise AI',
    title: 'Full-stack AI for complex enterprises',
    subtitle:
      "When your problem is too big for an off-the-shelf tool. We scope, design, build, integrate, and support production AI systems end-to-end.",
    included: [
      { icon: MessagesSquare, title: 'Discovery & scoping', desc: 'A workshop to map your workflows, systems, constraints, and goals before a line is written.' },
      { icon: Workflow, title: 'Architecture & approval', desc: 'We design the system and get your sign-off before building.' },
      { icon: Cpu, title: 'Custom LLM integration', desc: "OpenAI, Claude, Gemini, or fine-tuned models — chosen to fit your needs and budget." },
      { icon: Server, title: 'On-premise options', desc: 'Private cloud or on-premise deployment for data residency and control.' },
      { icon: ShieldCheck, title: 'Security & compliance', desc: 'Security review, PII handling, access controls, and compliance support.' },
      { icon: Users, title: 'Dedicated support', desc: 'SLA-backed support and an ongoing retainer for iteration and maintenance.' },
    ],
    useCases: [
      'Complex multi-step workflows',
      'Multiple system integrations',
      'Strict compliance requirements',
      'High data volumes',
      'Need for dedicated support',
      'Custom or fine-tuned model deployments',
    ],
    integrations: ['OpenAI', 'Anthropic', 'Gemini', 'On-premise', 'Private Cloud', 'Custom Infra'],
    industries: ['Banking & Fintech', 'Insurance', 'Healthcare', 'Legal'],
    ctaTitle: "Tell us what you're building.",
  },
};

export const solutionOrder = [
  'conversational-agents',
  'workflow-agents',
  'data-agents',
  'enterprise',
];
